// Lib
import ReactDOM from 'react-dom';
import { NativeTypes } from 'react-dnd-html5-backend';
import { defer, identity } from 'lodash/fp';
import { branch, compose, withHandlers } from 'recompose';

// Utils
import { handleFileDrop } from '../../../components/tools/files/fileDropTargetDecorator';
import { getElementId, getImageProp } from '../../../../common/elements/utils/elementPropertyUtils';
import { getNewTransactionId } from '../../../utils/undoRedo/undoRedoTransactionManager';

// Constants
import { DRAG_PREVIEW_ID } from '../../../reducers/draggingConstants';
import imageDropTargetDecorator from '../../image/imageDropTargetDecorator';

const onDropHandler = (props, monitor, component) => {
    const {
        elementId,
        currentBoardId,
        dispatchUpdateElement,
        dispatchMoveElementsToTrash,
        dispatchSwitchConnectedElementParents,
        dispatchAcceptElementAttachmentUndo,
    } = props;

    const transactionId = getNewTransactionId();

    if (monitor.getItemType() === NativeTypes.FILE) {
        dispatchAcceptElementAttachmentUndo({ id: elementId, transactionId });
        return handleFileDrop(props, monitor, component);
    }
    const droppedElement = monitor.getItem().element;
    const droppedElementId = getElementId(droppedElement);
    const droppedImage = getImageProp(droppedElement);

    // Update this element with the image from the dropped element
    dispatchUpdateElement({
        id: elementId,
        changes: { image: droppedImage },
        sync: true,
        transactionId,
    });

    // If image element has been created (not dragged directly from the image popup):
    // - Move any connected lines or comments from the dropped element over to the main element
    // - Delete the dropped element after a delay to ensure the drop handling has been finished
    if (droppedElementId !== DRAG_PREVIEW_ID) {
        dispatchSwitchConnectedElementParents({
            initialConnectedId: droppedElementId,
            newConnectedId: elementId,
            transactionId,
        });

        defer(() => dispatchMoveElementsToTrash({ elementId: droppedElementId, currentBoardId, transactionId }));
    }

    // Prevent lists or the canvas from handling the drop
    return {
        handledBy: ReactDOM.findDOMNode(component),
    };
};

/**
 * This decorator is used to allow image elements or native image files to be hoverable and droppable
 * on top of the drop target, in order to replace the image of the element.
 *
 * Currently used to replace images in file and link image previews.
 */
export default () =>
    compose(
        withHandlers({
            uploadFile:
                ({ element, dispatchUploadFile }) =>
                (file, transactionId) =>
                    dispatchUploadFile(getElementId(element), file, { onlyReplaceImage: true }, transactionId),
        }),
        branch(({ isPresentational }) => !isPresentational, imageDropTargetDecorator({ onDropHandler }), identity),
    );
